import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase';

/**
 * Load a lead document directly from Firestore for contextual prefill into the
 * Calculator or Quote workspace.
 *
 * Used when one of those modules is launched from the Customer Profile (via a
 * `?leadId=` URL param). Reads the same source the unified profile uses —
 * `companies/{companyId}/leads/{leadId}` — so prefill stays consistent with
 * what the CSR sees on the profile.
 *
 * Returns a flat, calculator/quote-friendly shape so callers don't have to
 * know about lead-doc internals. Splits `name` into first/last when
 * `firstName`/`lastName` are missing, and normalizes both string and object
 * address shapes.
 *
 * TODO(company-field): When the lead schema grows a `company` field, surface
 * it here so both Calculator and QuoteWorkspace can prefill it automatically.
 */
export async function loadLeadForPrefill(companyId, leadId) {
  if (!companyId || !leadId) {
    return { success: false, lead: null };
  }

  try {
    const leadRef = doc(db, 'companies', companyId, 'leads', leadId);
    const snap = await getDoc(leadRef);
    if (!snap.exists()) {
      return { success: false, lead: null };
    }

    const data = snap.data() || {};

    // Name: prefer explicit firstName/lastName; fall back to splitting `name`.
    let firstName = data.firstName || '';
    let lastName = data.lastName || '';
    if (!firstName && !lastName && typeof data.name === 'string' && data.name.trim()) {
      const parts = data.name.trim().split(/\s+/);
      firstName = parts[0] || '';
      lastName = parts.slice(1).join(' ') || '';
    }

    // Address: string OR object form.
    let line1 = '';
    let city = '';
    let state = '';
    let zip = '';
    const addr = data.address;
    if (typeof addr === 'string') {
      line1 = addr;
    } else if (addr && typeof addr === 'object') {
      line1 = addr.line1
        || addr.street
        || `${addr.streetNumber || ''} ${addr.streetName || ''}`.trim();
      city = addr.city || '';
      state = addr.state || '';
      zip = addr.zip || '';
    }

    return {
      success: true,
      lead: {
        id: snap.id,
        firstName,
        lastName,
        // TODO(company-field): map data.company once present on the schema.
        company: data.company || '',
        phone: data.phone || '',
        email: data.email || '',
        address: line1,
        city,
        state,
        zip,
        // Verified Google metadata (placeId, lat, lng, formattedAddress,
        // source, verifiedAt) — Calculator uses this to auto-center the map.
        location: data.location || null,
      },
    };
  } catch (err) {
    console.warn('[loadLeadForPrefill] failed to load lead', err);
    return { success: false, lead: null };
  }
}
